import { api } from "@/lib/apiClient";

export const PROJECT_KORREKTUR_FALLTYPEN = [
  "leeres_duplikat",
  "verteilte_akte",
  "falsches_material",
  "doppelbuchung",
] as const;

export type ProjectKorrekturFalltyp = (typeof PROJECT_KORREKTUR_FALLTYPEN)[number];

export const PROJECT_KORREKTUR_FALLTYP_LABELS: Record<ProjectKorrekturFalltyp, string> = {
  leeres_duplikat: "Leeres Duplikat",
  verteilte_akte: "Verteilte Akte",
  falsches_material: "Falsches Material",
  doppelbuchung: "Doppelbuchung",
};

export const PROJECT_KORREKTUR_FALLTYP_HINTS: Record<ProjectKorrekturFalltyp, string> = {
  leeres_duplikat: "Ein Projekt ist die echte Akte, das zweite ist praktisch leer.",
  verteilte_akte: "Ein Job in zwei Hüllen: Stunden hier, Material oder Notizen dort.",
  falsches_material: "Das Projekt stimmt, nur Material oder Bestellung hängt falsch.",
  doppelbuchung: "Dieselbe Arbeit steht auf beiden Projekten — Stunden nicht zusammenlegen.",
};

export interface ProjectKorrekturItem {
  id: number;
  label: string;
  status?: string | null;
  dokument_typ?: string | null;
}

export interface ProjectKorrekturInventory {
  auftrag_id: number;
  auftragsnummer: string;
  beschreibung?: string | null;
  kunde?: string | null;
  workflow_status?: string | null;
  commissioned: boolean;
  can_ablehnen: boolean;
  hero_project_id?: number | null;
  merged_into_auftrag_id?: number | null;
  timesheet_count: number;
  timesheets_movable: ProjectKorrekturItem[];
  timesheets_locked: ProjectKorrekturItem[];
  employee_time_count: number;
  order_count: number;
  orders: ProjectKorrekturItem[];
  gd_count: number;
  has_rechnung: boolean;
  documents: ProjectKorrekturItem[];
  material_anforderungen_count: number;
}

export interface ProjectKorrekturPreview {
  left: ProjectKorrekturInventory;
  right: ProjectKorrekturInventory;
  recommended_leading_id: number;
  defaults: Record<string, { move_hours: boolean; seal_reference: boolean }>;
  falltypen: { id: ProjectKorrekturFalltyp; label: string }[];
}

export interface ProjectKorrekturResult {
  id: number;
  falltyp: ProjectKorrekturFalltyp | string;
  leading_auftrag_id: number;
  reference_auftrag_id: number;
  leading_auftragsnummer: string;
  reference_auftragsnummer: string;
  protocol_text: string;
  moved_timesheet_ids: number[];
  moved_employee_time_ids: number[];
  documented_order_ids: number[];
  documented_gd_ids: number[];
  sealed: boolean;
  sealed_via?: string | null;
  note_leading_id?: number | null;
  note_reference_id?: number | null;
}

export function defaultMoveHours(falltyp: ProjectKorrekturFalltyp): boolean {
  return falltyp === "leeres_duplikat" || falltyp === "verteilte_akte";
}

export function defaultSealReference(falltyp: ProjectKorrekturFalltyp): boolean {
  return falltyp !== "falsches_material";
}

export function previewProjectKorrektur(
  auftragId: number,
  otherAuftragId: number,
): Promise<ProjectKorrekturPreview> {
  const params = new URLSearchParams({ other_auftrag_id: String(otherAuftragId) });
  return api.get(
    `/auftraege/${encodeURIComponent(String(auftragId))}/korrektur/preview?${params}`,
  );
}

export function executeProjectKorrektur(
  auftragId: number,
  payload: {
    other_auftrag_id: number;
    leading_auftrag_id: number;
    falltyp: ProjectKorrekturFalltyp;
    begruendung: string;
    move_hours?: boolean;
    seal_reference?: boolean;
  },
): Promise<ProjectKorrekturResult> {
  return api.post(`/auftraege/${encodeURIComponent(String(auftragId))}/korrektur`, payload);
}

export function projectDetailPath(auftragId: number): string {
  return `/projekte/${encodeURIComponent(String(auftragId))}`;
}
